import { useContext, useEffect, useState } from 'react'
import { SVGProps } from 'react'

import { animated, useTransition } from '@react-spring/web'
import styled from 'styled-components'

import { workoutCompletionTransition } from '~/animation/Workout.animation'
import { HeaderTypes, LIMITED_WORKOUT_GAME_TOTAL } from '~/constants'
import { AuthContext } from '~/context/AuthContext'
import { GameContext } from '~/context/GameContext'
import { WorkoutMetaNotNull } from '~/hooks/useTodaysWorkout'
import WorkoutComplete from '~/images/Workout/workoutComplete.svg'

interface WorkoutDonutProps {
  workoutMeta: WorkoutMetaNotNull
  checkmarkDelay?: number
  headerType?: HeaderTypes
}

export type WorkoutStatus = [
  Exclude<WorkoutPhaseStatus, WorkoutPhaseStatus.Locked>,
  Exclude<WorkoutPhaseStatus, WorkoutPhaseStatus.Locked>,
  WorkoutPhaseStatus,
  WorkoutPhaseStatus,
  Exclude<WorkoutPhaseStatus, WorkoutPhaseStatus.Locked>,
]

export enum WorkoutPhaseStatus {
  Inactive,
  Active,
  Complete,
  Locked,
}

const WorkoutDonut: React.FC<WorkoutDonutProps> = ({ workoutMeta, headerType, checkmarkDelay = 1100 }) => {
  const [workoutFinished, setWorkoutFinished] = useState(false)
  const { hasPremium } = useContext(AuthContext)
  const { slug } = useContext(GameContext)

  const { totalGamesCount, remainingGameCount, pivotBySlug, allPremiumSlots } = workoutMeta

  // Show the next segment outline on the workout card
  let showNextSegmentOutline = true

  // Don't show the next segment outline on the post game screen
  if (headerType && headerType === HeaderTypes.postWorkoutGame) {
    showNextSegmentOutline = false
  }

  // Don't show the segment outline during replay
  if (headerType && headerType === HeaderTypes.preWorkoutGame) {
    const currentWorkoutSlot = pivotBySlug[slug]

    if (currentWorkoutSlot?.lastGamePlay) {
      showNextSegmentOutline = false
    }
  }

  // Derives a WorkoutStatus array
  const workoutStatus: WorkoutStatus = allPremiumSlots.map((slot, index): WorkoutPhaseStatus => {
    if (!hasPremium && (index === 2 || index === 3)) {
      return WorkoutPhaseStatus.Locked
    }
    if (!slot.lastGamePlay) {
      return WorkoutPhaseStatus.Inactive
    }
    return WorkoutPhaseStatus.Complete
  }) as WorkoutStatus

  let lastPhaseComplete = workoutStatus.lastIndexOf(WorkoutPhaseStatus.Complete)
  if (!hasPremium && lastPhaseComplete === 1) {
    lastPhaseComplete = LIMITED_WORKOUT_GAME_TOTAL
  }
  if (lastPhaseComplete <= totalGamesCount && showNextSegmentOutline) {
    workoutStatus[lastPhaseComplete + 1] = WorkoutPhaseStatus.Active
  }

  useEffect(() => {
    if (remainingGameCount <= 0) {
      // triggers animation to checkmark
      setWorkoutFinished(true)
    } else {
      // allows donut to transition in reverse from the checkmark to the segmented workout donut (perhaps unnecessary)
      setWorkoutFinished(false)
    }
  }, [remainingGameCount])

  /* Animation */
  const finishWorkoutTransition = useTransition(
    workoutFinished,
    workoutCompletionTransition(workoutFinished, checkmarkDelay),
  )
  /*************/

  return (
    <WorkoutDonutContainer>
      {finishWorkoutTransition((styles, workoutComplete) =>
        workoutComplete ? (
          <animated.div style={styles}>
            <WorkoutComplete width='100%' height='100%' title='workout-complete' />
          </animated.div>
        ) : (
          <animated.div style={styles}>
            <Donut>
              <DonutFrame />
              <WorkoutSlots />
              {workoutStatus.map((status: WorkoutPhaseStatus, index) => {
                return (
                  <Segment
                    key={`WorkoutPhase ${index}`}
                    $workoutPhase={index}
                    $isComplete={status === WorkoutPhaseStatus.Complete}
                    $isActive={status === WorkoutPhaseStatus.Active}
                    $isLocked={status === WorkoutPhaseStatus.Locked}
                  />
                )
              })}
            </Donut>
          </animated.div>
        ),
      )}
    </WorkoutDonutContainer>
  )
}

interface SegmentProps extends SVGProps<SVGPathElement> {
  $workoutPhase: number
  $isActive?: boolean
  $isComplete?: boolean
  $isLocked?: boolean
}

const Segment = styled.path.attrs<Pick<SegmentProps, '$isLocked' | '$workoutPhase'>>(({ $isLocked, $workoutPhase }) => {
  if (!$isLocked) {
    return unlockedAttributes
  }
  if ($workoutPhase === 2) {
    return {
      ...lockedAttributes,
      d: WORKOUT_DONUT_PATHS.LockedPhase2,
    }
  }
  return {
    ...lockedAttributes,
    d: WORKOUT_DONUT_PATHS.LockedPhase3,
  }
})<SegmentProps>`
  transform-origin: 50% 50%;
  /* Using a strange number here, '359.6', rather than 360 to avoid tiny effect of background color leaking around segment edges.
     The svg path is the same for each segment, so we rotate each segment around the donut as necessary. 
     Locked segments do not need rotation.
  */
  transform: ${({ $workoutPhase, $isLocked }) => ($isLocked ? '' : `rotate(${(359.6 / 5) * $workoutPhase}deg)`)};
  stroke: ${({ theme, $isActive, $isComplete }) =>
    $isActive || $isComplete ? theme.colors.system.primaryAccent : 'none'};
  stroke-width: 5px;
  fill: ${({ theme, $isComplete }) => ($isComplete ? theme.colors.system.primaryAccent : '')};
  animation: ${({ $isComplete, $isActive }) => {
    if ($isComplete) return 'fadeIn 1s'
    if ($isActive) return 'strokeFadeIn 1s'
    return ''
  }};
  @keyframes strokeFadeIn {
    0% {
      stroke-width: 3px;
      stroke-opacity: 0;
    }
    100% {
      stroke-width: 5px;
      stroke-opacity: 1;
    }
  }
  @keyframes fadeIn {
    0% {
      fill-opacity: 0;
    }
    100% {
      fill-opacity: 1;
    }
  }
`

const enum WORKOUT_DONUT_PATHS {
  DonutSpokes = 'M155.625 83C155.625 42.8903 123.11 10.375 83 10.375C42.8903 10.375 10.375 42.8903 10.375 83C10.375 123.11 42.8903 155.625 83 155.625C123.11 155.625 155.625 123.11 155.625 83ZM112.566 83.0027C112.566 66.6723 99.3277 53.4339 82.9973 53.4339C66.6669 53.4339 53.4286 66.6723 53.4286 83.0027C53.4286 99.333 66.6669 112.571 82.9973 112.571C99.3277 112.571 112.566 99.333 112.566 83.0027ZM23.1442 56.7478C22.5168 58.1783 23.2959 59.8125 24.781 60.2968L46.9864 67.5374C48.3198 67.9722 49.7557 67.3176 50.4021 66.073C53.0323 61.0086 56.8202 56.6008 61.491 53.2267C66.1603 49.8536 71.5333 47.6427 77.1657 46.7364C78.5551 46.5128 79.6296 45.3482 79.6296 43.941V20.5958C79.6296 19.0365 78.3219 17.7902 76.7697 17.9388C65.2442 19.0426 54.1758 23.1938 44.7265 30.0198C35.2466 36.868 27.8106 46.1093 23.1442 56.7478ZM43.7251 131.625C42.8116 132.887 41.0229 133.131 39.8523 132.102C31.1254 124.433 24.5687 114.547 20.9124 103.437C17.2551 92.3235 16.6599 80.4724 19.1289 69.1171C19.4615 67.5877 21.0567 66.7226 22.5447 67.2078L44.7619 74.4523C46.0903 74.8854 46.8642 76.2525 46.66 77.6347C45.8288 83.2606 46.3112 89.0321 48.1064 94.4872C49.9161 99.9863 52.9902 104.952 57.0482 109.003C58.0453 109.998 58.2346 111.573 57.4087 112.715L43.7251 131.625ZM49.649 135.824C48.7293 137.095 49.0685 138.883 50.4286 139.665C60.3797 145.385 71.6989 148.404 83.2533 148.359C94.7931 148.315 106.075 145.218 115.973 139.435C117.321 138.647 117.652 136.868 116.736 135.603L103.039 116.685C102.216 115.549 100.67 115.234 99.4147 115.861C94.3848 118.373 88.8197 119.709 83.1434 119.731C77.5083 119.753 71.9728 118.478 66.9529 116.04C65.7018 115.432 64.1728 115.753 63.3575 116.88L49.649 135.824ZM122.607 131.324C123.525 132.591 125.325 132.83 126.493 131.789C135.055 124.158 141.489 114.379 145.096 103.409C148.73 92.3562 149.336 80.5724 146.909 69.272C146.58 67.7392 144.982 66.8702 143.492 67.3562L121.264 74.604C119.94 75.0358 119.166 76.3958 119.364 77.7743C120.165 83.3501 119.676 89.0642 117.899 94.4676C116.133 99.8407 113.159 104.705 109.239 108.706C108.261 109.703 108.083 111.264 108.902 112.395L122.607 131.324ZM142.917 56.9041C143.54 58.3338 142.76 59.9636 141.278 60.447L119.082 67.6843C117.744 68.1205 116.304 67.4602 115.661 66.2088C113.07 61.1705 109.336 56.7741 104.725 53.3902C100.138 50.0248 94.8573 47.7873 89.3074 46.8187C87.9357 46.5793 86.8828 45.4221 86.8828 44.0296V20.6495C86.8828 19.0817 88.2044 17.8323 89.7638 17.9947C101.263 19.1923 112.28 23.4243 121.662 30.3092C131.003 37.1636 138.321 46.3528 142.917 56.9041Z',
  WorkoutSlots = 'M24.781 60.2968C23.2959 59.8125 22.5168 58.1783 23.1442 56.7478C27.8106 46.1093 35.2466 36.868 44.7265 30.0198C54.1758 23.1938 65.2442 19.0426 76.7697 17.9388C78.3219 17.7902 79.6296 19.0365 79.6296 20.5958V43.941C79.6296 45.3482 78.5551 46.5128 77.1657 46.7364C71.5333 47.6427 66.1603 49.8536 61.491 53.2267C56.8202 56.6008 53.0323 61.0086 50.4021 66.073C49.7557 67.3176 48.3198 67.9722 46.9864 67.5374L24.781 60.2968ZM39.8523 132.102C41.0229 133.131 42.8116 132.887 43.7251 131.625L57.4087 112.715C58.2346 111.573 58.0453 109.998 57.0482 109.003C52.9902 104.952 49.9161 99.9863 48.1064 94.4872C46.3112 89.0321 45.8288 83.2606 46.66 77.6347C46.8642 76.2525 46.0903 74.8854 44.7619 74.4523L22.5447 67.2078C21.0567 66.7226 19.4615 67.5877 19.1289 69.1171C16.6599 80.4724 17.2551 92.3235 20.9124 103.437C24.5687 114.547 31.1254 124.433 39.8523 132.102ZM50.4286 139.665C49.0685 138.883 48.7293 137.095 49.649 135.824L63.3575 116.88C64.1728 115.753 65.7018 115.432 66.9529 116.04C71.9728 118.478 77.5083 119.753 83.1434 119.731C88.8197 119.709 94.3848 118.373 99.4147 115.861C100.67 115.234 102.216 115.549 103.039 116.685L116.736 135.603C117.652 136.868 117.321 138.647 115.973 139.435C106.075 145.218 94.7931 148.315 83.2533 148.359C71.6989 148.404 60.3797 145.385 50.4286 139.665ZM126.493 131.789C125.325 132.83 123.525 132.591 122.607 131.324L108.902 112.395C108.083 111.264 108.261 109.703 109.239 108.706C113.159 104.705 116.133 99.8407 117.899 94.4676C119.676 89.0642 120.165 83.3501 119.364 77.7743C119.166 76.3958 119.94 75.0358 121.264 74.604L143.492 67.3562C144.982 66.8702 146.58 67.7392 146.909 69.272C149.336 80.5724 148.73 92.3562 145.096 103.409C141.489 114.379 135.055 124.158 126.493 131.789ZM141.278 60.447C142.76 59.9636 143.54 58.3338 142.917 56.9041C138.321 46.3528 131.003 37.1636 121.662 30.3092C112.28 23.4243 101.263 19.1923 89.7638 17.9947C88.2044 17.8323 86.8828 19.0817 86.8828 20.6495V44.0296C86.8828 45.4221 87.9357 46.5793 89.3074 46.8187C94.8573 47.7873 100.138 50.0248 104.725 53.3902C109.336 56.7741 113.07 61.1705 115.661 66.2088C116.304 67.4602 117.744 68.1205 119.082 67.6843L141.278 60.447Z',
  LockedPhase2 = 'M79.0901 130.866C79.0901 128.088 81.3423 125.836 84.1206 125.836C86.8989 125.836 89.1511 128.088 89.1511 130.866V132.121C90.2314 132.523 91.001 133.564 91.001 134.784V139.324C91.001 140.893 89.7293 142.164 88.1606 142.164H79.8414C78.2727 142.164 77.001 140.893 77.001 139.324V134.784C77.001 133.476 77.886 132.374 79.0901 132.044V130.866ZM86.7841 130.866V131.944H81.4571V130.866C81.4571 129.395 82.6496 128.203 84.1206 128.203C85.5916 128.203 86.7841 129.395 86.7841 130.866ZM85.7005 135.636C85.7005 136.235 85.2414 136.829 84.5434 137.065L85.5243 139.469C85.6138 139.827 85.3431 140.173 84.9741 140.173H83.0241C82.6552 140.173 82.3844 139.827 82.4739 139.469L83.3505 137.065C82.8428 136.906 82.2978 136.235 82.2978 135.636C82.2978 134.697 83.0595 133.935 83.9991 133.935C84.9388 133.935 85.7005 134.697 85.7005 135.636Z',
  LockedPhase3 = 'M30.0901 94.8662C30.0901 92.0879 32.3423 89.8357 35.1206 89.8357C37.8989 89.8357 40.1511 92.0879 40.1511 94.8662V96.1214C41.2314 96.5234 42.001 97.564 42.001 98.7843V103.324C42.001 104.893 40.7293 106.164 39.1606 106.164H30.8414C29.2727 106.164 28.001 104.893 28.001 103.324V98.7843C28.001 97.4757 28.886 96.3738 30.0901 96.0444V94.8662ZM37.7841 94.8662V95.9439H32.4571V94.8662C32.4571 93.3952 33.6496 92.2027 35.1206 92.2027C36.5916 92.2027 37.7841 93.3952 37.7841 94.8662ZM36.7005 99.6363C36.7005 100.235 36.2414 100.829 35.5434 101.065L36.5243 103.469C36.6138 103.827 36.3431 104.173 35.9741 104.173H34.0241C33.6552 104.173 33.3844 103.827 33.4739 103.469L34.3505 101.065C33.8428 100.906 33.2978 100.235 33.2978 99.6363C33.2978 98.6966 34.0595 97.9349 34.9991 97.9349C35.9388 97.9349 36.7005 98.6966 36.7005 99.6363Z',
  UnlockedPhase = 'M141.083 57.7027C141.211 57.9964 141.075 58.4094 140.658 58.5456L118.462 65.7828C118.135 65.8894 117.672 65.7471 117.439 65.2943C114.708 59.9816 110.77 55.3459 105.908 51.7777C101.072 48.2291 95.5033 45.8698 89.6513 44.8485C89.1558 44.762 88.8828 44.3701 88.8828 44.0296V20.6495C88.8828 20.2076 89.2368 19.9506 89.5566 19.9839C100.704 21.1449 111.384 25.2475 120.479 31.9216C129.534 38.5663 136.628 47.4743 141.083 57.7027Z',
}

const WorkoutDonutContainer = styled.div`
  aspect-ratio: 1/1;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`

const Donut = styled.svg.attrs(() => ({
  width: '100%',
  viewBox: '0 0 166 164',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
}))``

const DonutFrame = styled.path.attrs(() => ({
  d: WORKOUT_DONUT_PATHS.DonutSpokes,
  fillRule: 'evenodd',
  clipRule: 'evenodd',
}))`
  fill: white;
`

const WorkoutSlots = styled.path.attrs(() => ({
  d: WORKOUT_DONUT_PATHS.WorkoutSlots,
  fillRule: 'evenodd',
  clipRule: 'evenodd',
}))`
  fill: ${({ theme }) => theme.colors.neutral[100]};
`

const lockedAttributes = {
  fillRule: 'evenodd',
  clipRule: 'evenodd',
  stroke: 'none',
  // lock is white
  fill: 'white',
}

const unlockedAttributes = {
  d: WORKOUT_DONUT_PATHS.UnlockedPhase,
  fillRule: 'evenodd',
  clipRule: 'evenodd',
}

export default WorkoutDonut
